import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const LEVEL_COLORS = {
  platine: "from-purple-500/30 to-fuchsia-500/30 border-purple-500/50 text-purple-300",
  or: "from-amber-400/30 to-yellow-500/30 border-amber-500/50 text-amber-300",
  argent: "from-zinc-400/30 to-zinc-500/30 border-zinc-400/50 text-zinc-200",
  bronze: "from-orange-400/30 to-amber-600/30 border-orange-500/50 text-orange-300",
  partenaire: "from-emerald-400/30 to-teal-500/30 border-emerald-500/50 text-emerald-300",
};

const STATUS_COLORS = {
  prospect: "border-zinc-700 text-zinc-400",
  contacte: "border-blue-500/40 text-blue-300",
  negociation: "border-amber-500/40 text-amber-300",
  confirme: "border-emerald-500/40 text-emerald-300",
  paye: "border-emerald-600/60 text-emerald-200",
  refus: "border-red-500/40 text-red-300",
};

export default function SponsorsModule() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [levels, setLevels] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [plan, setPlan] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [proposalText, setProposalText] = useState(null);
  const [roiSponsor, setRoiSponsor] = useState(null); // current sponsor for ROI dialog
  const [roiCompetitions, setRoiCompetitions] = useState([]);
  const [roiCompId, setRoiCompId] = useState("");
  const [roiData, setRoiData] = useState(null); // { sponsor, deliveries, total, delivered_count, completion_pct }
  const [roiReport, setRoiReport] = useState(null); // { subject, body, stats }
  const [roiBusy, setRoiBusy] = useState(false);
  const [form, setForm] = useState({
    business_name: "", contact_name: "", contact_email: "", contact_phone: "", website: "",
    level: "or", amount_cad: 0, in_kind_value_cad: 0, year: new Date().getFullYear(),
    status: "prospect", notes: "", why_them: "",
  });

  const loadAll = async () => {
    try {
      const [ref, list, p] = await Promise.all([
        api.get("/sponsors/levels"),
        api.get(`/sponsors?year=${year}`),
        api.get(`/sponsors/yearly-plan?year=${year}`),
      ]);
      setLevels(ref.data.levels);
      setStatuses(ref.data.statuses);
      setData(list.data);
      setPlan(p.data);
    } catch {
      toast.error("Erreur chargement sponsors");
    }
  };

  useEffect(() => { loadAll(); }, [year]);

  const submit = async () => {
    if (!form.business_name || !form.level) {
      toast.error("Nom et niveau requis");
      return;
    }
    try {
      if (editTarget) {
        await api.patch(`/sponsors/${editTarget}`, form);
        toast.success(`✅ ${form.business_name} mis à jour`);
      } else {
        await api.post("/sponsors", form);
        toast.success(`✅ ${form.business_name} ajouté`);
      }
      setCreateOpen(false);
      setEditTarget(null);
      setForm({ ...form, business_name: "", contact_name: "", contact_email: "", contact_phone: "", website: "", amount_cad: 0, in_kind_value_cad: 0, status: "prospect", notes: "", why_them: "" });
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const deleteSponsor = async (sid, name) => {
    if (!window.confirm(`Supprimer ${name} ?`)) return;
    try {
      await api.delete(`/sponsors/${sid}`);
      toast.success("Supprimé");
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const openEdit = (s) => {
    setEditTarget(s.id);
    setForm({
      business_name: s.business_name || "",
      contact_name: s.contact_name || "",
      contact_email: s.contact_email || "",
      contact_phone: s.contact_phone || "",
      website: s.website || "",
      level: s.level || "or",
      amount_cad: s.amount_cad || 0,
      in_kind_value_cad: s.in_kind_value_cad || 0,
      year: s.year || year,
      status: s.status || "prospect",
      notes: s.notes || "",
      why_them: s.why_them || "",
    });
    setCreateOpen(true);
  };

  const generateProposal = async (sid) => {
    try {
      const r = await api.get(`/sponsors/${sid}/proposal-text`);
      setProposalText(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const openRoi = async (sponsor) => {
    setRoiSponsor(sponsor);
    setRoiData(null);
    setRoiReport(null);
    setRoiCompId("");
    try {
      const r = await api.get("/mini-competitions");
      const comps = Array.isArray(r.data) ? r.data : (r.data?.competitions || []);
      setRoiCompetitions(comps);
      if (comps.length > 0) {
        const defaultComp = comps[0].id;
        setRoiCompId(defaultComp);
        await loadRoi(sponsor.id, defaultComp);
      }
    } catch (e) {
      toast.error("Erreur chargement compétitions");
    }
  };

  const loadRoi = async (sid, cid) => {
    if (!cid) return;
    setRoiBusy(true);
    try {
      const r = await api.get(`/sponsors/${sid}/roi?competition_id=${cid}`);
      setRoiData(r.data);
      setRoiReport(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur chargement ROI");
    } finally {
      setRoiBusy(false);
    }
  };

  const toggleDelivery = async (did, currentlyDelivered) => {
    try {
      await api.patch(`/sponsors/roi/deliveries/${did}`, { delivered: !currentlyDelivered });
      await loadRoi(roiSponsor.id, roiCompId);
    } catch (e) {
      toast.error("Erreur maj livraison");
    }
  };

  const updateDeliveryField = async (did, field, value) => {
    try {
      await api.patch(`/sponsors/roi/deliveries/${did}`, { [field]: value });
      await loadRoi(roiSponsor.id, roiCompId);
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const generateRoiReport = async () => {
    if (!roiSponsor || !roiCompId) return;
    setRoiBusy(true);
    try {
      const r = await api.post(`/sponsors/${roiSponsor.id}/roi/report?competition_id=${roiCompId}`);
      setRoiReport(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur rapport");
    } finally {
      setRoiBusy(false);
    }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié 📋");
  };

  const sponsorsByLevel = useMemo(() => {
    const out = {};
    if (data?.sponsors) {
      data.sponsors.forEach((s) => {
        out[s.level] = out[s.level] || [];
        out[s.level].push(s);
      });
    }
    return out;
  }, [data]);

  return (
    <div className="space-y-6" data-testid="sponsors-module">
      <div className="border border-zinc-800 rounded-2xl p-6 bg-gradient-to-br from-zinc-950 to-black">
        <div className="text-amber-400 text-[10px] tracking-[0.4em] mb-2">RÉSEAU KAIZEN · COMMANDITAIRES</div>
        <h1 className="text-3xl font-bold tracking-tight">Plan de commandites</h1>
        <p className="text-zinc-500 text-sm mt-2">Sponsors par niveau, objectifs annuels, dossier prêt à envoyer. Mercedes/Porsche : on commence solide, on grandit ensemble.</p>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <Label className="text-xs">Année</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 w-32" data-testid="year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2026, 2027, 2028, 2029].map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
            </SelectContent>
          </Select>
          <Button onClick={() => { setEditTarget(null); setCreateOpen(true); setForm({ ...form, year }); }} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="add-sponsor-btn">
            + Ajouter un sponsor
          </Button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sponsors-kpis">
          <KPI label="Sponsors total" value={data.total} />
          <KPI label="$ Confirmés" value={`${(data.total_confirmed_cad || 0).toLocaleString()} $`} accent />
          <KPI label="$ Pipeline" value={`${(data.total_pending_cad || 0).toLocaleString()} $`} />
          <KPI label="In-kind" value={`${(data.total_in_kind_cad || 0).toLocaleString()} $`} />
        </div>
      )}

      {/* Levels with sponsors */}
      <div className="space-y-4">
        {levels.map((lv) => {
          const sponsors = sponsorsByLevel[lv.id] || [];
          const colorClass = LEVEL_COLORS[lv.id] || "border-zinc-700 text-zinc-300";
          return (
            <div key={lv.id} className={`border rounded-2xl p-5 bg-gradient-to-br ${colorClass}`} data-testid={`level-section-${lv.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <h3 className="text-xl font-bold">{lv.label} <span className="text-sm opacity-60">({lv.min_cad}$+)</span></h3>
                  <div className="text-xs opacity-70 mt-1">{sponsors.length}/{lv.max_slots_per_event} places max par événement</div>
                </div>
                <div className="text-right">
                  <div className="text-xs opacity-70">Avantages :</div>
                  <div className="text-xs">{lv.benefits.length} bénéfices</div>
                </div>
              </div>
              <details className="mt-3">
                <summary className="cursor-pointer text-xs opacity-70 hover:opacity-100">Voir tous les avantages →</summary>
                <ul className="mt-2 space-y-1 text-xs opacity-80">
                  {lv.benefits.map((b, i) => <li key={i}>• {b}</li>)}
                </ul>
              </details>

              <div className="mt-4 grid md:grid-cols-2 gap-2">
                {sponsors.length === 0 ? (
                  <div className="text-xs opacity-50 italic col-span-2 py-3">Aucun sponsor à ce niveau pour {year}.</div>
                ) : (
                  sponsors.map((s) => (
                    <div key={s.id} className="border border-white/10 rounded-lg p-3 bg-black/30 flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-white truncate">{s.business_name}</div>
                        <div className="text-[10px] opacity-60">{s.contact_name || "—"} · {s.contact_email || "—"}</div>
                        <div className="flex gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[s.status] || ""}`}>{s.status}</Badge>
                          <span className="text-[10px] text-amber-300 font-semibold">{(s.amount_cad || 0).toLocaleString()}$</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <button onClick={() => generateProposal(s.id)} className="text-[10px] text-amber-300 hover:text-amber-200" data-testid={`proposal-btn-${s.id}`}>📄 Dossier</button>
                        <button onClick={() => openRoi(s)} className="text-[10px] text-emerald-300 hover:text-emerald-200" data-testid={`roi-btn-${s.id}`}>📊 ROI</button>
                        <button onClick={() => openEdit(s)} className="text-[10px] text-zinc-400 hover:text-white">✏️ Edit</button>
                        <button onClick={() => deleteSponsor(s.id, s.business_name)} className="text-[10px] text-red-400 hover:text-red-300">🗑️</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Yearly Plan */}
      {plan && (
        <div className="border border-zinc-800 rounded-2xl p-6 bg-zinc-950/40" data-testid="yearly-plan">
          <div className="text-amber-400 text-[10px] tracking-[0.4em] mb-3">PLAN D'ACTION {year}</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {plan.quarterly_actions.map((q, i) => (
              <div key={i} className="border border-zinc-800 rounded-xl p-4 bg-black/40">
                <div className="text-sm font-bold text-amber-400 mb-2">{q.quarter}</div>
                <ul className="space-y-1 text-xs text-zinc-300">
                  {q.actions.map((a, ai) => <li key={ai} className="flex gap-1"><span className="text-amber-500">▸</span> <span>{a}</span></li>)}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-6 grid md:grid-cols-5 gap-2">
            {Object.entries(plan.goals_vs_actuals).map(([lid, g]) => {
              const pct = g.goal_count > 0 ? Math.round((g.actual_count / g.goal_count) * 100) : 0;
              return (
                <div key={lid} className="border border-zinc-800 rounded-xl p-3 bg-black/40" data-testid={`goal-${lid}`}>
                  <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">{lid}</div>
                  <div className="text-2xl font-bold text-zinc-100 mt-1">{g.actual_count}<span className="text-sm text-zinc-500">/{g.goal_count}</span></div>
                  <div className="text-[10px] text-zinc-500">{(g.actual_cad || 0).toLocaleString()}$ / {g.goal_cad.toLocaleString()}$</div>
                  <div className="mt-2 h-1 bg-zinc-900 rounded-full overflow-hidden">
                    <div className={`h-full ${pct >= 100 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={createOpen} onOpenChange={(v) => { if (!v) { setCreateOpen(false); setEditTarget(null); } }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-xl" data-testid="sponsor-dialog">
          <DialogHeader><DialogTitle>{editTarget ? "Modifier" : "Ajouter"} un sponsor</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
            <div><Label className="text-xs">Nom de l'entreprise *</Label><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="bg-black border-zinc-800" data-testid="form-business-name" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Niveau *</Label>
                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                  <SelectTrigger className="bg-black border-zinc-800" data-testid="form-level"><SelectValue /></SelectTrigger>
                  <SelectContent>{levels.map((l) => (<SelectItem key={l.id} value={l.id}>{l.label} ({l.min_cad}$+)</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Statut</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="bg-black border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent>{statuses.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Montant ($ CAD)</Label><Input type="number" value={form.amount_cad} onChange={(e) => setForm({ ...form, amount_cad: Number(e.target.value) })} className="bg-black border-zinc-800" /></div>
              <div><Label className="text-xs">In-kind ($ estimé)</Label><Input type="number" value={form.in_kind_value_cad} onChange={(e) => setForm({ ...form, in_kind_value_cad: Number(e.target.value) })} className="bg-black border-zinc-800" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Contact</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="bg-black border-zinc-800" /></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="bg-black border-zinc-800" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Téléphone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="bg-black border-zinc-800" /></div>
              <div><Label className="text-xs">Site web</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="bg-black border-zinc-800" /></div>
            </div>
            <div><Label className="text-xs">Pourquoi eux ?</Label><Textarea value={form.why_them} onChange={(e) => setForm({ ...form, why_them: e.target.value })} className="bg-black border-zinc-800 min-h-[60px]" placeholder="Pourquoi ce sponsor a du sens pour KAIZEN…" /></div>
            <div><Label className="text-xs">Notes internes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-black border-zinc-800 min-h-[50px]" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateOpen(false); setEditTarget(null); }}>Annuler</Button>
            <Button onClick={submit} className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="form-submit-sponsor">{editTarget ? "Mettre à jour" : "Ajouter"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Proposal Dialog */}
      <Dialog open={!!proposalText} onOpenChange={(v) => !v && setProposalText(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl" data-testid="proposal-dialog">
          <DialogHeader><DialogTitle>📄 Dossier commandite</DialogTitle></DialogHeader>
          {proposalText && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
              <div>
                <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-1">SUJET EMAIL</div>
                <div className="text-sm text-zinc-200 font-medium">{proposalText.subject}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-1">CORPS DE L'EMAIL</div>
                <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-sans bg-black/30 p-3 rounded">{proposalText.body}</pre>
              </div>
              <Button onClick={() => copyText(`${proposalText.subject}\n\n${proposalText.body}`)} className="bg-amber-500 hover:bg-amber-400 text-black w-full" data-testid="copy-proposal-btn">📋 Copier sujet + corps</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ROI Dialog */}
      <Dialog open={!!roiSponsor} onOpenChange={(v) => { if (!v) { setRoiSponsor(null); setRoiData(null); setRoiReport(null); } }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl" data-testid="roi-dialog">
          <DialogHeader>
            <DialogTitle>📊 ROI Sponsor — {roiSponsor?.business_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            {/* Competition selector */}
            <div className="flex items-center gap-2 flex-wrap">
              <Label className="text-xs">Compétition</Label>
              <Select value={roiCompId} onValueChange={(v) => { setRoiCompId(v); if (roiSponsor) loadRoi(roiSponsor.id, v); }}>
                <SelectTrigger className="bg-black border-zinc-800 min-w-[260px]" data-testid="roi-comp-select"><SelectValue placeholder="Choisir compétition" /></SelectTrigger>
                <SelectContent>
                  {roiCompetitions.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name} · {c.date}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {roiCompetitions.length === 0 && (
                <span className="text-xs text-zinc-500 italic">Aucune compétition créée</span>
              )}
            </div>

            {roiBusy && <div className="text-xs text-zinc-500">Chargement…</div>}

            {/* Progression */}
            {roiData && (
              <div className="border border-emerald-500/30 rounded-xl p-4 bg-emerald-500/5" data-testid="roi-progress">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] tracking-[0.3em] text-emerald-400">PROGRESSION LIVRAISONS</div>
                  <div className="text-xs text-emerald-300 font-bold">{roiData.delivered_count}/{roiData.total} · {roiData.completion_pct}%</div>
                </div>
                <div className="h-2 bg-black/50 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${roiData.completion_pct}%` }} />
                </div>
              </div>
            )}

            {/* Checklist deliveries */}
            {roiData?.deliveries?.length > 0 && (
              <div className="space-y-2" data-testid="roi-deliveries-list">
                <div className="text-[10px] tracking-[0.3em] text-amber-400">CHECKLIST AVANTAGES À LIVRER</div>
                {roiData.deliveries.map((d) => (
                  <div key={d.id} className={`border rounded-lg p-3 ${d.delivered ? "border-emerald-500/40 bg-emerald-500/5" : "border-zinc-800 bg-black/40"}`} data-testid={`delivery-row-${d.id}`}>
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={d.delivered}
                        onChange={() => toggleDelivery(d.id, d.delivered)}
                        className="mt-1 accent-emerald-500 h-4 w-4 cursor-pointer"
                        data-testid={`delivery-check-${d.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm ${d.delivered ? "text-emerald-200 line-through" : "text-zinc-100"}`}>{d.benefit_text}</div>
                        {d.delivered && (
                          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                            <Input
                              defaultValue={d.proof_url || ""}
                              onBlur={(e) => { if (e.target.value !== (d.proof_url || "")) updateDeliveryField(d.id, "proof_url", e.target.value); }}
                              placeholder="🔗 URL preuve (photo, post FB/IG, screenshot)"
                              className="bg-black border-zinc-800 text-xs h-8"
                              data-testid={`delivery-proof-${d.id}`}
                            />
                            <div className="flex gap-1">
                              <Input
                                defaultValue={d.metric_label || ""}
                                onBlur={(e) => { if (e.target.value !== (d.metric_label || "")) updateDeliveryField(d.id, "metric_label", e.target.value); }}
                                placeholder="Métrique (ex: Vues post FB)"
                                className="bg-black border-zinc-800 text-xs h-8 flex-1"
                              />
                              <Input
                                type="number"
                                defaultValue={d.metric_value || ""}
                                onBlur={(e) => { const v = e.target.value === "" ? null : Number(e.target.value); if (v !== (d.metric_value ?? null)) updateDeliveryField(d.id, "metric_value", v); }}
                                placeholder="Valeur"
                                className="bg-black border-zinc-800 text-xs h-8 w-24"
                                data-testid={`delivery-metric-${d.id}`}
                              />
                            </div>
                            {d.delivered_at && (
                              <div className="md:col-span-2 text-[10px] text-emerald-400/70">✅ Livré le {new Date(d.delivered_at).toLocaleString("fr-CA")}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {roiData && roiData.deliveries?.length === 0 && (
              <div className="text-xs text-zinc-500 italic py-4">Aucune livraison initialisée pour cette compétition.</div>
            )}

            {/* Generate report */}
            {roiData && (
              <div className="border-t border-zinc-800 pt-4">
                <Button
                  onClick={generateRoiReport}
                  disabled={roiBusy}
                  className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold w-full"
                  data-testid="generate-roi-report-btn"
                >
                  📊 Générer le rapport ROI complet
                </Button>
              </div>
            )}

            {/* Report preview */}
            {roiReport && (
              <div className="border border-emerald-500/40 rounded-xl p-4 bg-black/40 space-y-3" data-testid="roi-report-preview">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                  <RoiStat label="Athlètes" value={roiReport.stats.registrations} />
                  <RoiStat label="Écoles" value={roiReport.stats.schools} />
                  <RoiStat label="Médailles" value={roiReport.stats.medals} />
                  <RoiStat label="Reach total" value={roiReport.stats.total_reach.toLocaleString()} accent />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] text-emerald-400 mb-1">SUJET EMAIL</div>
                  <div className="text-sm text-zinc-200 font-medium">{roiReport.subject}</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] text-emerald-400 mb-1">CORPS DU RAPPORT</div>
                  <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-sans bg-black/30 p-3 rounded max-h-64 overflow-y-auto">{roiReport.body}</pre>
                </div>
                <Button
                  onClick={() => copyText(`${roiReport.subject}\n\n${roiReport.body}`)}
                  className="bg-emerald-500 hover:bg-emerald-400 text-black w-full"
                  data-testid="copy-roi-report-btn"
                >
                  📋 Copier sujet + rapport
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RoiStat({ label, value, accent }) {
  return (
    <div className={`rounded-lg border p-2 ${accent ? "border-emerald-500/40 bg-emerald-500/10" : "border-zinc-800 bg-zinc-950/50"}`}>
      <div className="text-[9px] tracking-[0.3em] text-zinc-500 uppercase">{label}</div>
      <div className={`text-lg font-bold mt-1 ${accent ? "text-emerald-300" : "text-zinc-100"}`}>{value}</div>
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <div className={`rounded-xl border p-3 ${accent ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800 bg-zinc-950/50"}`}>
      <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${accent ? "text-amber-300" : "text-zinc-100"}`}>{value}</div>
    </div>
  );
}
